<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
  <div class="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
          <h2 class="text-lg font-semibold">{{ isMobile() ? '提前还款' : '提前还款计算器（一次性部分提前还款）' }}</h2>
          <el-segmented class="EPI mobile-wrap w-full sm:w-auto" v-model="method" :options="methodOptions" />
        </div>
      </template>
  <el-alert type="info" :closable="false" class="mb-3" title="用途：基于原还款计划，模拟一次性部分提前还款，支持缩短期数或降低月供两种策略，比较利息节省与期数变化。" />
  <!-- 示例方案选择 -->
  <div class="flex flex-col sm:flex-row sm:items-center gap-3">
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-600">示例方案</span>
          <el-select v-model="selectedExample" placeholder="选择示例方案（可选）" class="w-full sm:w-80" clearable @change="onExampleChange">
            <el-option v-for="opt in exampleOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </div>
      </div>

  <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <el-form-item :label="`贷款金额（${symbol}）`">
            <el-input v-model.number="amountUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="年化利率（%）">
            <el-input v-model.number="form.annualRatePct" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数（月）">
            <el-input v-model.number="form.months" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="`提前还款金额（${symbol}）`">
            <el-input v-model.number="prepayAmountUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写A）</div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 将操作按钮单独占一排 -->
      <div class="flex gap-3 justify-start">
        <el-button type="primary" @click="onBuild">查看原还款计划</el-button>
        <el-button @click="onReset">重置</el-button>
      </div>

      <FormulaPanel
        title="提前还款（一次性部分）"
        :formulas="prepayFormulas"
        :variables="variablesMap"
        :notes="['选择策略：缩短期数(固定PMT)或降低月供(固定n)','重算基于剩余本金与月利率']"
      />
      <el-collapse-transition>
        <div v-if="baseSchedule" class="mt-4 space-y-4">
        <div class="space-y-2">
          <div class="flex flex-col md:flex-row md:items-end gap-4">
            <div class="flex-1">
              <div class="text-xs text-gray-600 mb-1">提前还款发生期（第几期进行一次性还款）</div>
              <el-input-number v-model="prepayMonth" :min="1" :max="baseSchedule.rows.length" />
            </div>
            <div class="flex-1">
              <div class="text-xs text-gray-600 mb-1">策略选择</div>
              <el-segmented class="mobile-wrap" v-model="strategy" :options="strategyOptions" />
            </div>
          </div>
          <div class="text-xs text-gray-500 leading-snug">
            说明：左侧计数器为提前还款发生的期数（从 1 开始计数）。“缩短期数”表示保持月供不变，缩短剩余期数；“降低月供”表示保持剩余期数不变，重新计算更低的月供。
          </div>
        </div>
            <div >
              <el-button type="success" size="large" class="w-full md:w-auto" @click="onApply">进行提前还款</el-button>
            </div>
          <el-descriptions title="原计划汇总" :column="3" border>
          <el-descriptions-item :label="`总还款（${symbol}）`">{{ d2(baseSchedule.totalPayment) }}</el-descriptions-item>
          <el-descriptions-item :label="`总利息（${symbol}）`">{{ d2(baseSchedule.totalInterest) }}</el-descriptions-item>
          <el-descriptions-item label="期数">{{ baseSchedule.rows.length }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="adjusted" class="space-y-4">
          <el-descriptions title="调整后汇总" :column="3" border>
            <el-descriptions-item :label="`总还款（${symbol}）`">{{ d2(adjusted.totalPayment) }}</el-descriptions-item>
            <el-descriptions-item :label="`总利息（${symbol}）`">{{ d2(adjusted.totalInterest) }}</el-descriptions-item>
            <el-descriptions-item label="期数">{{ adjusted.rows.length }}</el-descriptions-item>
          </el-descriptions>
          <el-alert :closable="false" type="success" :title="`利息节省：${d2(interestSaved)} ${symbol}；期数变化：${termChange} 期`" />

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <el-card shadow="never" body-class="p-0">
              <template #header>
                <div class="text-sm text-gray-700">原计划分解（本金/利息）</div>
              </template>
              <v-chart class="h-56 md:h-72" :option="chartOptionBase" autoresize />
            </el-card>
            <el-card shadow="never" body-class="p-0">
              <template #header>
                <div class="text-sm text-gray-700">调整后分解（本金/利息）</div>
              </template>
              <v-chart class="h-56 md:h-72" :option="chartOptionAdjusted" autoresize />
            </el-card>
          </div>
        </div>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { use } from 'echarts/core'
import VChart from 'vue-echarts'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { generateSchedule, applyLumpSumPrepayment, round2 } from '@/lib/loanEngine'
import { useCurrency } from '@/composables/useCurrency'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
function isMobile() {
  return /Android|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
use([CanvasRenderer, BarChart, GridComponent, TooltipComponent, LegendComponent])
const methodOptions = [
  { label: '等额本息', value: 'EPI' },
  { label: '等额本金', value: 'EP' }
]
const strategyOptions = [
  { label: '缩短期数', value: 'reduce-term' },
  { label: '降低月供', value: 'reduce-payment' }
]

const method = ref('EPI')
const strategy = ref('reduce-term')
const form = ref({ amount: 500000, annualRatePct: 4.2, months: 360, prepayAmount: 50000 })
const baseSchedule = ref(null)
const adjusted = ref(null)
const prepayMonth = ref(24)

// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const amountUI = computed({
  get: () => toDisplay(form.value.amount),
  set: (v) => { form.value.amount = toBase(v) }
})
const prepayAmountUI = computed({
  get: () => toDisplay(form.value.prepayAmount),
  set: (v) => { form.value.prepayAmount = toBase(v) }
})

// 示例方案
const selectedExample = ref(null)
const exampleOptions = [
  { label: '房贷（等额本息）30年，年化4.2%，第24期预还5万，缩短期数', value: 'mortgage-epi' },
  { label: '消金（等额本金）24期，年化8%，第6期预还1万，降低月供', value: 'consumer-ep' }
]
const onExampleChange = (val) => {
  if (!val) return
  if (val === 'mortgage-epi') {
    method.value = 'EPI'
    strategy.value = 'reduce-term'
    form.value = { amount: 500000, annualRatePct: 4.2, months: 360, prepayAmount: 50000 }
    prepayMonth.value = 24
  } else if (val === 'consumer-ep') {
    method.value = 'EP'
    strategy.value = 'reduce-payment'
    form.value = { amount: 80000, annualRatePct: 8, months: 24, prepayAmount: 10000 }
    prepayMonth.value = 6
  }
}

const onBuild = () => {
  adjusted.value = null
  baseSchedule.value = generateSchedule({ amount: form.value.amount, annualRatePct: form.value.annualRatePct, months: form.value.months, method: method.value })
}

const onReset = () => {
  method.value = 'EPI'
  strategy.value = 'reduce-term'
  form.value = { amount: 0, annualRatePct: 0, months: 0, prepayAmount: 0 }
  baseSchedule.value = null
  adjusted.value = null
  prepayMonth.value = 1
}

const onApply = () => {
  if (!baseSchedule.value) return
  const r = Number(form.value.annualRatePct) / 12 / 100
  adjusted.value = applyLumpSumPrepayment(baseSchedule.value, {
    monthIndex: prepayMonth.value,
    amount: form.value.prepayAmount,
    strategy: strategy.value,
    baseMonthlyRate: r
  })
}

const interestSaved = computed(() => {
  if (!baseSchedule.value || !adjusted.value) return 0
  return round2(baseSchedule.value.totalInterest - adjusted.value.totalInterest)
})
const termChange = computed(() => {
  if (!baseSchedule.value || !adjusted.value) return 0
  return baseSchedule.value.rows.length - adjusted.value.rows.length
})

const chartOpt = (sch) => {
  if (!sch) return {}
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['本金', '利息'] },
    grid: { left: 24, right: 16, top: 24, bottom: 24, containLabel: true },
    xAxis: { type: 'category', data: sch.rows.map(r => r.period) },
    yAxis: { type: 'value' },
    series: [
      { name: '本金', type: 'bar', stack: 'total', data: sch.rows.map(r => toDisplay(r.principal)) },
      { name: '利息', type: 'bar', stack: 'total', data: sch.rows.map(r => toDisplay(r.interest)) },
    ]
  }
}

// 使用不同的配色区分左右两张图
const chartOptionBase = computed(() => {
  const opt = chartOpt(baseSchedule.value)
  if (!opt || !opt.series) return opt
  return {
    ...opt,
    color: ['#4f46e5', '#60a5fa'] // 蓝紫-蓝，用于原计划
  }
})
const chartOptionAdjusted = computed(() => {
  const opt = chartOpt(adjusted.value)
  if (!opt || !opt.series) return opt
  return {
    ...opt,
    color: ['#90EE90', '#f59e0b'] // 浅绿/金色，用于调整后
  }
})

const prepayFormulas = [
  String.raw`\text{Balance}_{k}^{\text{after}} = \max\{0,\ \text{Balance}_{k}^{\text{before}} - A_{pre}\}`,
  String.raw`\text{缩短期数：} n' = \left\lceil \dfrac{\ln\left(\dfrac{\text{PMT}}{\text{PMT} - \text{Balance}_{k}^{\text{after}}\cdot r_m}\right)}{\ln(1+r_m)} \right\rceil`,
  String.raw`\text{降低月供：} \text{PMT}' = \dfrac{\text{Balance}_{k}^{\text{after}}\cdot r_m (1+r_m)^{n-k+1}}{(1+r_m)^{n-k+1} - 1}`
]
const variablesMap = computed(() => ([
  { symbol: 'PV', label: `贷款本金（${symbol.value}）` },
  { symbol: 'r_m', label: '月利率', desc: '年化/12' },
  { symbol: 'n', label: '原期数（月）' },
  { symbol: 'A_{pre}', label: `提前还款金额（${symbol.value}）` },
  { symbol: 'k', label: '提前还款发生期（从1开始）' },
]))

// 显示用：金额保留两位小数（先换算到当前币种）
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

</script>
<style scoped>
.mobile-wrap :deep(.el-segmented__group) {
  flex-wrap: wrap;
  row-gap: 6px;
}
.EPI{
  --el-segmented-item-selected-color: var(--el-text-color-primary);
  --el-segmented-item-selected-bg-color: #ffd100;
  --el-border-radius-base: 16px;
}
</style>